import { CommonModule } from '@angular/common';
import { Component, EventEmitter, input, OnInit, Output, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { MarkantElementsModule } from '@markant/elements-angular';
import {
  BookingData,
  BookingDataWithUpgradeInfo,
  Cartridge,
  CartridgeVariant,
} from '@markant-platform/cartridge/model';
import { UpgradeServiceVariantsComponent } from './upgrade-service-variants/upgrade-service-variants.component';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BookingService, CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { CompleteOrganizationDataComponent } from './complete-org-data/complete-org-data.component';
import { AdditionalOrgDataForm, NOT_APPLICABLE, UpgradeServiceVariantForm } from './upgrade-forms.config';
import { deepEqual, RegexPattern } from '@markant-platform/shared/util-helpers';
import { VariantUpgradeOverviewComponent } from './variant-upgrade-overview/variant-upgrade-overview.component';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { OrganizationDetailData } from 'data-model';

@Component({
  selector: 'markant-platform-upgrade-variant-modal',
  templateUrl: './upgrade-variant-modal.component.html',
  styleUrls: ['./upgrade-variant-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MarkantElementsModule,
    ModalBridgeComponent,
    TranslocoModule,
    UpgradeServiceVariantsComponent,
    ReactiveFormsModule,
    CompleteOrganizationDataComponent,
    VariantUpgradeOverviewComponent,
  ],
  providers: [TranslocoModule],
})
export class UpgradeVariantModalComponent implements OnInit {
  organization = input.required<OrganizationDetailData>();
  booking = input.required<BookingDataWithUpgradeInfo>();
  @Output() closeDialog: EventEmitter<void> = new EventEmitter();
  @Output() bookingUpdated: EventEmitter<void> = new EventEmitter();

  currentStepIndex = signal<number>(0);
  responsibleUserEmail!: string;
  variantUpgradeForm: UpgradeServiceVariantForm;
  additionalOrgDataForm: AdditionalOrgDataForm;
  service!: Cartridge;
  upgradeCandidates: CartridgeVariant[] = [];

  showUpgradeVariantErrors = false;
  showAdditionalOrgaDataErrors = false;

  private readonly isLoadingSubject = new BehaviorSubject(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(
    private readonly cartridgesFacade: CartridgesFacade,
    private readonly bookingService: BookingService,
    private readonly organizationService: OrganizationService,
    private readonly snackbarService: SnackbarService
  ) {
    this.variantUpgradeForm = this.initVariantUpgradeForm();
    this.additionalOrgDataForm = this.initAdditionalOrgDataForm();
  }

  async ngOnInit(): Promise<void> {
    this.upgradeCandidates = await this.getUpgradeCandidates();
    this.patchVariantUpgradeForm();
    this.patchAdditionalOrgDataForm();
  }

  async nextStep(): Promise<void> {
    if (this.formsHaveErrors()) {
      return;
    }

    if (this.currentStepIndex() === 0) {
      this.currentStepIndex.update(currentStep => {
        const shouldSkipAdditionalOrgDataStep = this.variantUpgradeForm.controls.billingViaPlatform.value === 'false';
        return shouldSkipAdditionalOrgDataStep ? currentStep + 2 : currentStep + 1;
      });
    } else if (this.currentStepIndex() === 1) {
      this.currentStepIndex.update(currentStep => currentStep + 1);
    } else {
      this.isLoadingSubject.next(true);
      try {
        await firstValueFrom(this.updateOrganizationWithBillingInfo());
        await firstValueFrom(this.sendUpgradeRequest());

        this.snackbarService.showSuccessSnackbarWithTransloco(
          'upgrade.pfa.upgradeMessages.success',
          undefined,
          undefined,
          true
        );

        this.bookingUpdated.emit();
        this.closeDialog.emit();
      } finally {
        this.isLoadingSubject.next(false);
      }
    }
  }

  previousStep(): void {
    if (this.currentStepIndex() === 2) {
      this.currentStepIndex.update(currentStep => {
        const shouldSkipAdditionalOrgDataStep = this.variantUpgradeForm.controls.billingViaPlatform.value === 'false';
        return shouldSkipAdditionalOrgDataStep ? currentStep - 2 : currentStep - 1;
      });
    } else if (this.currentStepIndex() === 1) {
      this.currentStepIndex.update(currentStep => currentStep - 1);
    }
  }

  private sendUpgradeRequest(): Observable<BookingData> {
    const upgradeTargetVariant: CartridgeVariant = this.upgradeCandidates.find(
      variant => variant.variantId === this.variantUpgradeForm.controls.variant.value
    ) as CartridgeVariant;

    const hasIndividualPrice =
      this.variantUpgradeForm.controls.individualPrice.value !== NOT_APPLICABLE &&
      this.variantUpgradeForm.controls.individualPrice.value !== null;

    const bookingReference = this.variantUpgradeForm.controls.bookingReference.value;

    return this.bookingService.requestUpgrade(this.booking().id, {
      upgradeToVariantId: upgradeTargetVariant?.variantId,
      platformBilling: this.variantUpgradeForm.controls.billingViaPlatform.value === 'true',
      ...(hasIndividualPrice && {
        centAmount: +(this.variantUpgradeForm.controls.individualPrice.value as string),
      }),
      ...(bookingReference && {
        bookingReference,
      }),
      version: this.booking().version,
    });
  }

  private updateOrganizationWithBillingInfo(): Observable<OrganizationDetailData | undefined> {
    const updatedOrganizationData: OrganizationDetailData = {
      ...this.organization(),
      masterKin: this.organization().masterKin ?? (this.additionalOrgDataForm.controls.masterKIN.value as string),
      billingEmail:
        this.organization().billingEmail ?? (this.additionalOrgDataForm.controls.billingEmail.value as string),
      billingLanguageIsoCode:
        this.organization().billingLanguageIsoCode ??
        (this.additionalOrgDataForm.controls.billingLanguage.value as string),
    };

    if (deepEqual(this.organization(), updatedOrganizationData)) {
      return of(undefined);
    }

    return this.organizationService.setOrganizationDetailsById(
      this.organization().id as string,
      this.organization(),
      updatedOrganizationData
    );
  }

  private formsHaveErrors(): boolean {
    if (this.currentStepIndex() === 0) {
      this.showUpgradeVariantErrors = this.variantUpgradeForm.invalid;
      return this.showUpgradeVariantErrors;
    } else if (this.currentStepIndex() === 1) {
      this.showAdditionalOrgaDataErrors = this.additionalOrgDataForm.invalid;
      return this.showAdditionalOrgaDataErrors;
    } else {
      return false;
    }
  }

  private async getUpgradeCandidates(): Promise<CartridgeVariant[]> {
    const cartridge = (await firstValueFrom(
      this.cartridgesFacade.cartridgeById$(this.booking().cartridgeId)
    )) as Cartridge;

    this.service = cartridge;

    return this.service.variants.filter(variant =>
      this.booking().upgradableCartridgeVariantIds.includes(variant.variantId)
    );
  }

  private initVariantUpgradeForm(): UpgradeServiceVariantForm {
    return new FormGroup({
      cartridge: new FormControl<Cartridge | null>(null, [Validators.required]),
      variant: new FormControl<string | null>(null, [Validators.required]),
      billingViaPlatform: new FormControl<string | null>({ value: null, disabled: true }, [Validators.required]),
      individualPrice: new FormControl<string | null>({ value: null, disabled: true }),
      responsibleUserEmail: new FormControl<string | null>({ value: null, disabled: true }, [Validators.required]),
      bookingReference: new FormControl<string | null>(null),
    });
  }

  private initAdditionalOrgDataForm(): AdditionalOrgDataForm {
    return new FormGroup({
      masterKIN: new FormControl<string | null>(null, [
        Validators.required,
        Validators.pattern(RegexPattern.MASTER_KIN),
      ]),
      billingEmail: new FormControl<string | null>(null, [
        Validators.required,
        Validators.pattern(RegexPattern.EMAIL_PATTERN),
        Validators.pattern(RegexPattern.XSS_PATTERN),
      ]),
      billingLanguage: new FormControl<string | null>(null, [Validators.required]),
    });
  }

  private patchVariantUpgradeForm(): void {
    this.variantUpgradeForm.patchValue({
      cartridge: this.service,
      billingViaPlatform: `${!!this.booking().platformBilling}`,
      responsibleUserEmail: this.booking().responsibleUserEmail,
      bookingReference: this.booking().otherReferences,
    });
  }

  private patchAdditionalOrgDataForm(): void {
    // Patch values
    this.additionalOrgDataForm.patchValue({
      masterKIN: this.organization().masterKin ?? null,
      billingEmail: this.organization().billingEmail ?? null,
      billingLanguage: this.organization().billingLanguageIsoCode ?? null,
    });

    // Update allow editing´
    Object.keys(this.additionalOrgDataForm.controls).forEach((key: string) => {
      const control = this.additionalOrgDataForm.get(key) as FormControl;
      if (control.value) {
        control.disable();
      }
    });
  }
}
